<template>
  <div
    class="w-full overflow-hidden rounded focus:outline-none focus:ring-2 focus:ring-azul-profundo"
    :class="aspectClass"
    role="region"
    :aria-label="$t('vitrine.carousel.galleryAria')"
    tabindex="0"
  >
    <div
      ref="track"
      :class="trackClasses"
      @transitionend="onTransitionEnd"
      :style="{ transform: 'translateX(-' + currentIndex * 100 + '%)' }"
    >
      <div
        v-for="(img, i) in extendedImages"
        :key="'ext-' + i"
        class="min-w-full"
        :class="aspectClass"
      >
        <img
          :src="img"
          :alt="altForExt(i)"
          class="w-full h-full object-contain"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VitrineCarousel",
  props: {
    images: { type: Array, default: () => [] },
    intervalMs: { type: Number, default: 6000 },
    // 'square' | 'video' | 'wide'
    aspect: { type: String, default: "square" },
    // Base do alt; ex.: nome do produto
    altText: { type: String, default: "" },
  },
  data() {
    return {
      timer: null,
      currentIndex: 0, // será 1 quando houver loop
      animating: true, // controla transição ao fazer o snap
    };
  },
  computed: {
    aspectClass() {
      if (this.aspect === "video") return "aspect-video";
      if (this.aspect === "wide") return "aspect-[16/9]";
      return "aspect-square";
    },
    // Clona última e primeira para loop contínuo
    extendedImages() {
      const arr = Array.isArray(this.images) ? this.images : [];
      if (arr.length > 1) {
        return [arr[arr.length - 1], ...arr, arr[0]];
      }
      return arr;
    },
    // Liga/desliga transição na trilha
    trackClasses() {
      return this.animating
        ? "flex transition-transform duration-500 ease-in-out"
        : "flex transition-none";
    },
  },
  methods: {
    // Gera alt descritivo por imagem
    altFor(index) {
      if (this.altText) {
        const pos = index + 1;
        const total = this.images.length || 1;
        return `${this.altText} (${pos}/${total})`;
      }
      return this.$t("vitrine.carousel.alt");
    },
    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    start() {
      // só inicia se houver mais de uma imagem
      if (Array.isArray(this.images) && this.images.length > 1) {
        this.stop();
        this.scheduleNext();
      }
    },
    scheduleNext() {
      this.stop();
      this.timer = setTimeout(() => {
        this.goNext();
      }, this.intervalMs);
    },
    resetAndMaybeStart(newImages) {
      this.stop();
      if (Array.isArray(newImages) && newImages.length > 1) {
        // com clones: começa na 1 (primeiro item real)
        this.currentIndex = 1;
        this.animating = true;
        this.scheduleNext();
      } else {
        // 0 ou 1 imagem: sem autoplay
        this.currentIndex = 0;
        this.animating = true;
      }
    },
    // índice real (0..n-1) a partir da lista estendida
    realIndex(i) {
      const n = this.images.length;
      if (n <= 1) return 0;
      if (i === 0) return n - 1;
      if (i === n + 1) return 0;
      return i - 1;
    },
    // alt usando índice real
    altForExt(i) {
      return this.altFor(this.realIndex(i));
    },
    // avança e faz snap invisível no fim
    goNext() {
      if (this.extendedImages.length <= 1) return;
      // evita colisão de timers durante o snap
      this.stop();
      this.animating = true;
      this.currentIndex += 1;
    },
    // ao terminar a transição, verifica se está num clone
    onTransitionEnd(e) {
      // ignora eventos de filhos (img)
      if (!this.$refs.track || e.target !== this.$refs.track) return;

      const last = this.extendedImages.length - 1;

      if (this.currentIndex === last) {
        // clone do primeiro → snap invisível para índice real 1
        this.animating = false;
        this.currentIndex = 1;

        // força reflow para aplicar o snap sem transição
        this.$nextTick(() => {
          // leitura força o layout antes de reativar transição
          void this.$refs.track.offsetHeight;
          this.animating = true;
          this.scheduleNext();
        });
        return;
      }

      // avanço normal: agenda próximo passo
      this.scheduleNext();
    },
  },
  watch: {
    // Quando as imagens chegam de forma assíncrona, inicia o autoplay
    images: {
      immediate: true,
      handler(newVal) {
        this.resetAndMaybeStart(newVal);
      },
    },
    // Se o intervalo mudar, reinicia o autoplay
    intervalMs(newVal) {
      if (typeof newVal === "number" && newVal > 0) {
        this.scheduleNext();
      }
    },
  },
  beforeUnmount() {
    this.stop();
  },
};
</script>
